import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { useSystem } from "@/lib/system";
import { ArrowLeft, Save } from "lucide-react";

const Field = ({ label, children, span = 1 }) => (
  <div className={`col-span-${span}`}>
    <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1.5">{label}</label>
    {children}
  </div>
);

const ipt = "w-full bg-bg border border-line rounded-sm px-3 py-2 text-sm focus:border-cyan focus:outline-none focus:ring-1 focus:ring-cyan/40";

export default function TicketNew() {
  const nav = useNavigate();
  const { system: globalSystem } = useSystem();
  const [users, setUsers] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [groups, setGroups] = useState([]);
  const [tgChats, setTgChats] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [f, setF] = useState({
    system: globalSystem || "TARAPAY",
    service_type: "PAYIN",
    title: "", description: "",
    order_number: "", merchant_order_number: "", platform_order_number: "",
    rrn_number: "", va_number: "",
    transaction_type: "QRIS", customer_type: "FOREX",
    payment_channel: "Ayolinx",
    merchant_name: "", merchant_group: "", nmid: "", amount: 0, transaction_date: "",
    proof_url: "", customer_name: "", customer_contact: "",
    language: "ID", source: "MANUAL", priority: "NORMAL",
    assigned_to: "",
    telegram_mapping_id: "",
  });

  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)); }, []);
  useEffect(() => { api.get("/telegram/chats").then((r) => setTgChats(r.data)).catch(() => setTgChats([])); }, []);
  useEffect(() => {
    api.get("/merchants", { params: { system: f.system } }).then((r) => setMerchants(r.data));
  }, [f.system]);
  useEffect(() => {
    if (!f.merchant_name) { setGroups([]); return; }
    api.get("/merchant-groups", { params: { merchant: f.merchant_name, system: f.system } }).then((r) => setGroups(r.data));
  }, [f.merchant_name, f.system]);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const { telegram_mapping_id, ...body } = f;
      if (!body.assigned_to) delete body.assigned_to;
      if (telegram_mapping_id) {
        const chat = tgChats.find((c) => c.id === telegram_mapping_id);
        if (chat) {
          body.telegram_chat_id = chat.chat_id;
          body.telegram_chat_title = chat.chat_title || chat.merchant_name;
          if (chat.thread_id) {
            body.telegram_thread_id = chat.thread_id;
            body.telegram_thread_name = chat.thread_name || `Topic ${chat.thread_id}`;
          }
        }
      }
      const { data } = await api.post("/tickets", body);
      nav(`/tickets/${data.id}`);
    } catch (e2) { setErr(formatErr(e2)); }
    finally { setBusy(false); }
  };

  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  return (
    <div className="p-6 max-w-5xl animate-slide-up" data-testid="ticket-new-page">
      <button data-testid="back-btn" onClick={() => nav(-1)} className="text-xs text-slate-400 hover:text-cyan flex items-center gap-1.5 mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Back
      </button>
      <h1 className="text-2xl font-bold tracking-tight mb-1">Create New Ticket</h1>
      <p className="text-xs text-slate-400 mb-6">Catat inquiry transaksi dari Telegram/WhatsApp group ke sini.</p>

      <form onSubmit={submit} className="space-y-5" data-testid="ticket-form">
        <section className="bg-surface border border-line rounded-sm p-4 grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="col-span-2 md:col-span-3 text-[10px] uppercase tracking-[0.2em] text-cyan">Inquiry</div>
          <Field label="System">
            <select data-testid="ti-system" value={f.system} onChange={upd("system")} className={ipt}>
              <option value="TARAPAY">Tarapay</option>
              <option value="SATURNUS">Saturnuspay</option>
            </select>
          </Field>
          <Field label="Service Type">
            <select data-testid="ti-service-type" value={f.service_type} onChange={upd("service_type")} className={ipt}>
              <option value="PAYIN">Pay-In</option>
              <option value="PAYOUT">Payout</option>
              <option value="WITHDRAWAL">Withdrawal</option>
              <option value="TOPUP">Top-Up</option>
            </select>
          </Field>
          <Field label="Merchant / Customer">
            <input
              data-testid="ti-merchant-name"
              list="merchants-list"
              value={f.merchant_name}
              onChange={upd("merchant_name")}
              className={ipt}
              placeholder="Pilih atau ketik nama merchant"
            />
            <datalist id="merchants-list">
              {merchants.map((m) => <option key={m.id} value={m.name} />)}
            </datalist>
          </Field>
          <Field label="Merchant Group (opsional)">
            <input
              data-testid="ti-merchant-group"
              list="groups-list"
              value={f.merchant_group}
              onChange={upd("merchant_group")}
              className={`${ipt} font-mono`}
              placeholder="CP-001"
            />
            <datalist id="groups-list">
              {groups.map((g) => <option key={g.code} value={g.code}>{g.code} ({g.count})</option>)}
            </datalist>
          </Field>
          <Field label="Title (ringkas)" span={2}>
            <input data-testid="ti-title" required value={f.title} onChange={upd("title")} className={ipt} placeholder="QRIS pending Merchant XYZ - 250rb" />
          </Field>
          <Field label="Customer Name">
            <input data-testid="ti-customer-name" value={f.customer_name} onChange={upd("customer_name")} className={ipt} />
          </Field>
          <Field label="Customer Contact / Group">
            <input data-testid="ti-customer-contact" value={f.customer_contact} onChange={upd("customer_contact")} className={ipt} placeholder="@telegram_user or +6281..." />
          </Field>
          <Field label="Source">
            <select data-testid="ti-source" value={f.source} onChange={upd("source")} className={ipt}>
              <option value="TELEGRAM">Telegram</option>
              <option value="WHATSAPP">WhatsApp</option>
              <option value="EMAIL">Email</option>
              <option value="MANUAL">Manual</option>
            </select>
          </Field>
          <Field label="Language">
            <select data-testid="ti-language" value={f.language} onChange={upd("language")} className={ipt}>
              <option value="ID">Indonesia</option>
              <option value="ZH">中文</option>
              <option value="EN">English</option>
            </select>
          </Field>
          <Field label="Description" span={2}>
            <textarea data-testid="ti-description" rows={2} value={f.description} onChange={upd("description")} className={ipt} placeholder="Detail keluhan / catatan..." />
          </Field>
        </section>

        <section className="bg-surface border border-line rounded-sm p-4 grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="col-span-2 md:col-span-3 text-[10px] uppercase tracking-[0.2em] text-cyan">Transaction Details</div>
          <Field label="Customer Type">
            <select data-testid="ti-customer-type" value={f.customer_type} onChange={upd("customer_type")} className={ipt}>
              <option value="FOREX">Forex</option>
              <option value="GAMING">Gaming</option>
            </select>
          </Field>
          <Field label="Transaction Type">
            <select data-testid="ti-transaction-type" value={f.transaction_type} onChange={upd("transaction_type")} className={ipt}>
              <option value="QRIS">QRIS</option>
              <option value="VA">VA</option>
            </select>
          </Field>
          <Field label="Payment Channel">
            <select data-testid="ti-payment-channel" value={f.payment_channel} onChange={upd("payment_channel")} className={ipt}>
              <option value="Ayolinx">Ayolinx</option>
              <option value="Pakailink">Pakailink</option>
              <option value="Pearpaygame">Pearpaygame</option>
              <option value="Easylink">Easylink</option>
            </select>
          </Field>
          <Field label="Merchant Order Number">
            <input data-testid="ti-merchant-order" value={f.merchant_order_number} onChange={upd("merchant_order_number")} className={`${ipt} font-mono`} placeholder="dari merchant (yang customer kirim)" />
          </Field>
          <Field label="Platform Order Number (PAYIN/PAYOUT…)">
            <input data-testid="ti-platform-order" value={f.platform_order_number} onChange={upd("platform_order_number")} className={`${ipt} font-mono`} placeholder="PAYIN43xxxxx / PAYOUT… (dari Tarapay/Saturnus)" />
          </Field>
          <Field label="RRN Number (QRIS)">
            <input data-testid="ti-rrn-number" value={f.rrn_number} onChange={upd("rrn_number")} className={`${ipt} font-mono`} />
          </Field>
          <Field label="VA Number">
            <input data-testid="ti-va-number" value={f.va_number} onChange={upd("va_number")} className={`${ipt} font-mono`} />
          </Field>
          <Field label="NMID">
            <input data-testid="ti-nmid" value={f.nmid} onChange={upd("nmid")} className={`${ipt} font-mono`} />
          </Field>
          <Field label="Amount (IDR)">
            <input data-testid="ti-amount" type="number" value={f.amount} onChange={upd("amount")} className={`${ipt} font-mono`} />
          </Field>
          <Field label="Transaction Date/Time">
            <input data-testid="ti-transaction-date" type="datetime-local" value={f.transaction_date} onChange={upd("transaction_date")} className={ipt} />
          </Field>
          <Field label="Proof URL (Drive/Img)" span={2}>
            <input data-testid="ti-proof-url" value={f.proof_url} onChange={upd("proof_url")} className={ipt} placeholder="https://drive.google.com/..." />
          </Field>
        </section>        <section className="bg-surface border border-line rounded-sm p-4 grid grid-cols-2 gap-4">
          <div className="col-span-2 text-[10px] uppercase tracking-[0.2em] text-cyan">Assignment & Channel</div>
          <Field label="Priority">
            <select data-testid="ti-priority" value={f.priority} onChange={upd("priority")} className={ipt}>
              <option value="LOW">Low</option>
              <option value="NORMAL">Normal</option>
              <option value="HIGH">High</option>
              <option value="URGENT">Urgent</option>
            </select>
          </Field>
          <Field label="Assign to">
            <select data-testid="ti-assigned-to" value={f.assigned_to} onChange={upd("assigned_to")} className={ipt}>
              <option value="">Unassigned</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
            </select>
          </Field>
          <Field label="Reply via Telegram chat / topic (opsional)" span={2}>
            <select data-testid="ti-telegram-chat" value={f.telegram_mapping_id} onChange={upd("telegram_mapping_id")} className={ipt}>
              <option value="">— None (no Telegram link) —</option>
              {tgChats.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.chat_title || c.merchant_name}
                  {c.thread_id ? ` · 🧵 ${c.thread_name || `Topic ${c.thread_id}`}` : " · root"}
                  {` · ${c.system} · ${c.chat_id}`}
                </option>
              ))}
            </select>
            <div className="text-[10px] text-slate-500 mt-1">Pilih chat group / topic untuk mengaktifkan tombol &quot;Kirim ke Telegram&quot; di ticket detail. Reply otomatis landing di topic yang dipilih. Kosongkan kalau tidak perlu reply via Telegram.</div>
          </Field>
        </section>

        {err && <div data-testid="ticket-err" className="px-3 py-2 border border-rose-500/30 bg-rose-500/10 text-rose-400 text-xs rounded-sm">{err}</div>}

        <div className="flex gap-2">
          <button data-testid="ticket-submit-btn" type="submit" disabled={busy} className="bg-cyan text-bg font-semibold px-5 py-2.5 rounded-sm text-sm flex items-center gap-2 hover:bg-cyan-dark disabled:opacity-60">
            <Save className="w-4 h-4" /> {busy ? "Saving..." : "Create Ticket"}
          </button>
          <button type="button" onClick={() => nav(-1)} className="border border-line px-5 py-2.5 rounded-sm text-sm text-slate-300 hover:bg-surface2">Cancel</button>
        </div>
      </form>
    </div>
  );
}
